import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import useUserStore from "@store/useUserStore";
import SubHeader from "components/common/SubHeader";
import LoadingSpinner from "components/common/LoadingSpiner";
import CommonModal from "components/common/CommonModal";
import CoverPage from "components/common/CoverPage";
import BucketListSheet from "components/bucketlist/BucketListSheet";
import { useSnackbar } from "contexts/SnackbarContext";
import { updateUserData } from "api/user";
import {
    mergeCategoryOptions,
    getCategoryLabel,
    listBucketList,
    deleteBucketList,
    achieveBucketList,
} from "api/bucketList";
import "assets/scss/pages/bucketList.scss";

const ALL = "ALL";

const BucketList = () => {
    const userInfo = useUserStore((state) => state.userInfo);
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    const { showError, showSuccess } = useSnackbar();

    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState(ALL);
    const [includeAchieved, setIncludeAchieved] = useState(true);
    const [visible, setVisible] = useState(userInfo?.bucketlist_visible ?? false);

    const [sheetOpen, setSheetOpen] = useState(false);
    const [editId, setEditId] = useState(null); // 수정/완료 대상 id (null=생성)
    const [sheetCompleting, setSheetCompleting] = useState(false); // 완료 처리 모드
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAchieving, setIsAchieving] = useState(false);

    // silent=true 는 페이지 전체 로딩 스피너를 띄우지 않고 목록만 갱신한다.
    // 완료/수정 시트가 열린 채 사진을 업로드/삭제할 때 사용 — 스피너가 뜨면
    // 페이지가 통째로 언마운트되며 열려 있던 시트와 입력값이 사라진다.
    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setIsLoading(true);
        const list = await listBucketList();
        setItems(Array.isArray(list) ? list : []);
        if (!silent) setIsLoading(false);
    }, []);

    // 시트 내부(사진 그리드)에서의 목록 갱신 — 스피너 없이 조용히 새로고침
    const reloadSilently = useCallback(() => load({ silent: true }), [load]);

    useEffect(() => {
        if (userInfo === null) return;
        load();
        // 사용자(로그인) 기준으로만 로드 — 공개 토글 등 userInfo 부분 변경엔 재호출하지 않음
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.user_id, load]);

    useEffect(() => {
        setVisible(userInfo?.bucketlist_visible ?? false);
    }, [userInfo?.bucketlist_visible]);

    // 마스터 공개 토글 — pb_visible/cert_visible와 동일 패턴 (BE #449 §4-7)
    const handleVisibleToggle = async (next) => {
        const prev = visible;
        setVisible(next);
        try {
            const res = await updateUserData({ bucketlist_visible: next });
            if (res?.code !== "0000") throw new Error("update failed");
            setUserInfo({ ...userInfo, bucketlist_visible: next });
        } catch {
            setVisible(prev);
            showError("공개 설정 변경에 실패했어요");
        }
    };

    const filtered = useMemo(() => {
        const list = items.filter((it) => {
            if (!includeAchieved && it.is_achieved) return false;
            if (activeCategory !== ALL && it.category !== activeCategory)
                return false;
            return true;
        });
        // 공개 항목을 최상단에 배치 (그룹 내 기존 순서는 stable sort로 유지)
        return [...list].sort(
            (a, b) => (b.is_public ? 1 : 0) - (a.is_public ? 1 : 0)
        );
    }, [items, activeCategory, includeAchieved]);

    // 공개 항목 목록 (편집 중인 항목은 제외) — 3개 제한 판정 + 스왑 픽커에 사용
    const publicItems = useMemo(
        () => items.filter((it) => it.is_public && it.id !== editId),
        [items, editId]
    );

    // 칩 목록: 알려진 카테고리 + 데이터에 존재하는 미정의 카테고리를 합쳐 서버 값에 따라 가변 생성
    const categoryOptions = useMemo(
        () => mergeCategoryOptions(items.map((it) => it.category)),
        [items]
    );

    // id로만 보관 — 목록 새로고침 시 최신 item(사진 등 반영)으로 자동 파생
    const editItem = useMemo(
        () => items.find((it) => it.id === editId) ?? null,
        [items, editId]
    );

    // 생성 — 목표/카테고리/공개만
    const openCreate = () => {
        setEditId(null);
        setSheetCompleting(false);
        setSheetOpen(true);
    };

    // 수정 — 항목 클릭 시 바로 진입. 목표/카테고리/공개 (+ 달성 항목이면 달성일/사진/기록도 노출)
    const openEdit = (item) => {
        setEditId(item.id);
        setSheetCompleting(false);
        setSheetOpen(true);
    };

    // 완료 처리 — 시트에 달성일/사진/기록 노출, 저장 시 달성
    const openComplete = (item) => {
        setEditId(item.id);
        setSheetCompleting(true);
        setSheetOpen(true);
    };

    // 선택한 필터 칩을 가로 스크롤 영역 가운데로 이동 (맨 끝 칩도 눌렀을 때 스크롤이 보이도록)
    const centerChip = (el) =>
        el?.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
        });

    const handleAchieveClick = async (item) => {
        // 미달성 → 완료 시트(달성일·사진·기록)에서 저장 시 달성 처리
        if (!item.is_achieved) {
            openComplete(item);
            return;
        }
        // 이미 달성 → 즉시 달성 취소
        if (isAchieving) return;
        try {
            setIsAchieving(true);
            await achieveBucketList(item.id, null);
            showSuccess("달성을 취소했어요");
            await load();
        } catch {
            showError("달성 취소에 실패했어요");
        } finally {
            setIsAchieving(false);
        }
    };

    const submitDelete = async () => {
        if (!deleteTarget || isDeleting) return;
        try {
            setIsDeleting(true);
            await deleteBucketList(deleteTarget.id);
            showSuccess("삭제했어요");
            setDeleteTarget(null);
            await load();
        } catch {
            showError("삭제에 실패했어요");
        } finally {
            setIsDeleting(false);
        }
    };

    if (userInfo === null) return <CoverPage />;
    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="bucket-page">
            <SubHeader title="나의 버킷리스트" />

            <div className="bucket-toolbar">
                <label className="bucket-toolbar__visible inline-visibility-toggle">
                    <span className="inline-visibility-toggle__label">
                        프로필에 공개
                    </span>
                    <span className="inline-visibility-toggle__switch">
                        <input
                            type="checkbox"
                            checked={visible}
                            onChange={(e) => handleVisibleToggle(e.target.checked)}
                        />
                        <span className="inline-visibility-toggle__slider" />
                    </span>
                </label>
                <label className="bucket-toolbar__include">
                    <input
                        type="checkbox"
                        checked={includeAchieved}
                        onChange={(e) => setIncludeAchieved(e.target.checked)}
                    />
                    <span>달성한 항목 포함</span>
                </label>
            </div>

            <div className="bucket-chips" role="tablist">
                <button
                    type="button"
                    className={`bucket-chip${
                        activeCategory === ALL ? " bucket-chip--active" : ""
                    }`}
                    onClick={(e) => {
                        setActiveCategory(ALL);
                        centerChip(e.currentTarget);
                    }}
                >
                    전체
                </button>
                {categoryOptions.map((c) => (
                    <button
                        key={c.code}
                        type="button"
                        className={`bucket-chip${
                            activeCategory === c.code ? " bucket-chip--active" : ""
                        }`}
                        onClick={(e) => {
                            setActiveCategory(c.code);
                            centerChip(e.currentTarget);
                        }}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="bucket-empty">
                    <p className="bucket-empty__emoji" aria-hidden="true">
                        ✨
                    </p>
                    <p className="bucket-empty__title">
                        {items.length === 0
                            ? "아직 버킷리스트가 없어요"
                            : "이 카테고리엔 아직 버킷리스트가 없어요"}
                    </p>
                    <p className="bucket-empty__desc">
                        {items.length === 0
                            ? "이루고 싶은 순간을 하나씩 적어볼까요?"
                            : "새로운 버킷리스트를 추가해볼까요?"}
                    </p>
                    <button
                        type="button"
                        className="btn btn-purple500 bucket-empty__cta"
                        onClick={openCreate}
                    >
                        {items.length === 0
                            ? "첫 버킷리스트 추가하기"
                            : "버킷리스트 추가하기"}
                    </button>
                </div>
            ) : (
                <ul className="bucket-list">
                    {filtered.map((item) => {
                        const categoryLabel = getCategoryLabel(item.category);
                        return (
                            <li
                                key={item.id}
                                className={`bucket-item${
                                    item.is_achieved ? " bucket-item--achieved" : ""
                                }`}
                            >
                                <button
                                    type="button"
                                    className={`bucket-item__check${
                                        item.is_achieved
                                            ? " bucket-item__check--done"
                                            : ""
                                    }`}
                                    onClick={() => handleAchieveClick(item)}
                                    aria-label={
                                        item.is_achieved ? "달성 취소" : "달성 처리"
                                    }
                                    aria-pressed={item.is_achieved}
                                >
                                    ✓
                                </button>
                                <div
                                    className="bucket-item__body"
                                    onClick={() => openEdit(item)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            openEdit(item);
                                        }
                                    }}
                                >
                                    <div className="bucket-item__meta">
                                        {categoryLabel && (
                                            <span className="bucket-item__category">
                                                {categoryLabel}
                                            </span>
                                        )}
                                        {item.is_public && (
                                            <span className="bucket-item__public">
                                                공개
                                            </span>
                                        )}
                                    </div>
                                    <p className="bucket-item__title">
                                        {item.content}
                                    </p>
                                    {(item.target_date || item.achieved_at) && (
                                        <p className="bucket-item__date">
                                            {item.is_achieved && item.achieved_at
                                                ? `달성 ${item.achieved_at}`
                                                : item.target_date
                                                ? `목표 ${item.target_date}`
                                                : ""}
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="bucket-item__delete"
                                    onClick={() => setDeleteTarget(item)}
                                    aria-label="삭제"
                                >
                                    ✕
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <BucketListSheet
                isOpen={sheetOpen}
                onClose={() => setSheetOpen(false)}
                item={editItem}
                completing={sheetCompleting}
                onSaved={load}
                onChanged={reloadSilently}
                publicItems={publicItems}
            />

            <CommonModal
                isOpen={!!deleteTarget}
                onRequestClose={() => setDeleteTarget(null)}
                className="bucket-delete-modal"
            >
                <div className="bucket-modal-body">
                    <p className="bucket-modal-body__title">
                        이 버킷리스트를 삭제할까요?
                    </p>
                    <div className="bucket-modal-actions">
                        <button
                            type="button"
                            className="btn btn-gray200 btn-fit"
                            onClick={() => setDeleteTarget(null)}
                            disabled={isDeleting}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="btn btn-purple500 btn-fit"
                            onClick={submitDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? "삭제 중..." : "삭제"}
                        </button>
                    </div>
                </div>
            </CommonModal>

            {/* 바텀시트가 열려 있을 땐 FAB를 숨긴다. (반투명 백드롭 뒤로 비쳐 보이는 문제 방지) */}
            {!sheetOpen &&
                createPortal(
                    <div className="bucket-fab-wrapper">
                        <div className="bucket-fab-container">
                            <button
                                type="button"
                                className="bucket-fab"
                                onClick={openCreate}
                                aria-label="버킷리스트 추가"
                            >
                                +
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default BucketList;
